/*
 * TTJ Wishlist Core & Shared Functionality
 * Manages localStorage reads/writes safely with cross-tab syncing and event notifications.
 *
 * NOTE: Wishlist items are stored per browser/device in localStorage.
 * Data does not sync across different devices or browsers unless customer metafields or a server-side app is configured.
 */

(function() {
  'use strict';

  // The script can be requested by the header, the PDP and the wishlist page on the same
  // page. Only the first execution may attach listeners, otherwise every click would
  // toggle twice and cancel itself out.
  if (window.TTJWishlist) return;

  var STORAGE_KEY = 'ttj_wishlist_items';

  function getWishlist() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      
      // Sanitize: filter strings only, validate handle format, remove duplicates
      var clean = [];
      var handleRegex = /^[a-z0-9-]+$/;
      for (var i = 0; i < parsed.length; i++) {
        var item = parsed[i];
        if (typeof item === 'string') {
          item = item.trim();
          if (handleRegex.test(item) && clean.indexOf(item) === -1) {
            clean.push(item);
          }
        }
      }
      return clean;
    } catch (e) {
      console.warn('TTJ Wishlist: Error parsing storage, resetting cleanly.', e);
      return [];
    }
  }

  function setWishlist(items) {
    try {
      if (!Array.isArray(items)) items = [];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      dispatchChangeEvent(items);
    } catch (e) {
      console.error('TTJ Wishlist: Storage write error.', e);
    }
  }

  function toggleHandle(handle) {
    if (!handle || typeof handle !== 'string') return false;
    var list = getWishlist();
    var idx = list.indexOf(handle);
    var isSaved = false;

    if (idx > -1) {
      list.splice(idx, 1);
      isSaved = false;
    } else {
      list.push(handle);
      isSaved = true;
    }

    setWishlist(list);
    return isSaved;
  }

  function removeHandle(handle) {
    if (!handle || typeof handle !== 'string') return;
    var list = getWishlist();
    var idx = list.indexOf(handle);
    if (idx > -1) {
      list.splice(idx, 1);
      setWishlist(list);
    }
  }

  function isSaved(handle) {
    if (!handle) return false;
    var list = getWishlist();
    return list.indexOf(handle) > -1;
  }

  function dispatchChangeEvent(items) {
    try {
      var evt = new CustomEvent('ttj:wishlist-change', {
        detail: { items: items || getWishlist() }
      });
      window.dispatchEvent(evt);
    } catch (e) {}
  }

  function updateButtonState(btn, saved) {
    if (!btn) return;
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    
    var addLabel = btn.dataset.labelAdd || 'Add to wishlist';
    var removeLabel = btn.dataset.labelRemove || 'Remove from wishlist';
    btn.setAttribute('aria-label', saved ? removeLabel : addLabel);

    var outline = btn.querySelector('.icon-heart-outline');
    var filled = btn.querySelector('.icon-heart-filled');

    if (outline && filled) {
      outline.style.display = saved ? 'none' : 'block';
      filled.style.display = saved ? 'block' : 'none';
    }
  }

  // Header wishlist count bubble(s): any [data-wishlist-count] element is kept in step with
  // the saved list. Hidden while the list is empty.
  function updateCounts(list) {
    var total = list.length;
    var counters = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < counters.length; i++) {
      counters[i].textContent = total > 99 ? '99+' : String(total);
      counters[i].hidden = total === 0;
    }
    var links = document.querySelectorAll('[data-wishlist-link]');
    for (var j = 0; j < links.length; j++) {
      var base = links[j].getAttribute('data-label') || 'Wishlist';
      links[j].setAttribute('aria-label', total > 0 ? base + ' (' + total + ')' : base);
    }
  }

  function syncAllButtons() {
    var buttons = document.querySelectorAll('[data-wishlist-button]');
    var list = getWishlist();
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var handle = btn.dataset.productHandle;
      if (handle) {
        updateButtonState(btn, list.indexOf(handle) > -1);
      }
    }
    updateCounts(list);
  }

  // Product grids are re-rendered by Shopify filtering / sorting / pagination (innerHTML swaps),
  // which drops freshly-rendered cards in with their default (un-saved) heart state.
  // Re-sync whenever new wishlist buttons appear in the DOM.
  var syncQueued = false;
  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    var run = function() { syncQueued = false; syncAllButtons(); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else setTimeout(run, 16);
  }

  function nodeHasWishlistUi(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.hasAttribute('data-wishlist-button') || node.hasAttribute('data-wishlist-count')) return true;
    return !!(node.querySelector && node.querySelector('[data-wishlist-button], [data-wishlist-count]'));
  }

  function observeDomChanges() {
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (nodeHasWishlistUi(added[j])) { queueSync(); return; }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Click delegate for all wishlist buttons (PDP, product cards, wishlist page).
  // Capture phase + stopPropagation: a heart placed on a product card must only toggle the
  // wishlist and never reach the card's link / other card click handlers (no navigation).
  document.addEventListener('click', function(e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-wishlist-button]') : null;
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();
    var handle = btn.dataset.productHandle;
    if (handle) {
      toggleHandle(handle);
      syncAllButtons();
    }
  }, true);

  // Cross-tab storage sync
  window.addEventListener('storage', function(e) {
    if (e.key === STORAGE_KEY) {
      syncAllButtons();
      dispatchChangeEvent(getWishlist());
    }
  });

  // Event listener for custom change events
  window.addEventListener('ttj:wishlist-change', function() {
    syncAllButtons();
  });

  // Initialize on DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      syncAllButtons();
      observeDomChanges();
    });
  } else {
    syncAllButtons();
    observeDomChanges();
  }

  // Expose global TTJ Wishlist API
  window.TTJWishlist = {
    get: getWishlist,
    set: setWishlist,
    toggle: toggleHandle,
    remove: removeHandle,
    isSaved: isSaved,
    sync: syncAllButtons
  };
})();
